import React, {FC, memo} from 'react';
import styled from 'styled-components';

import {TimeFormatsEnum} from '../../../../../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {getTimeFormat} from '../../../../../../libs/shared/core/src/store/data/preferences/commonPreferencesSelectors';
import {getHasSortP2BySlaTimerFF} from '../../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {RelativeDateTimeFormatsEnum} from '../../../../../../libs/shared/ui/src/components/datetime/relativeDateTimeFormatter';
import {LegacyInteractionParent} from '../../../../../../libs/shared/ui/src/interactionTracking/interactionParent';
import {fontSizes} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ThemeContextType} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {CompactDateTimeFormatter} from '../../../../../../srcCommon/formatters/dateTime/compactDateTimeFormatter';
import {DateTimeFormatsEnum} from '../../../../../../srcCommon/formatters/dateTime/dateTimeFormatter';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {getDateTimeFormat} from '../../../../../store/data/localPreferences/localPreferencesSelectors';
import {InteractiveDateTimeFormatter} from '../../../../common/formatters/dateTime/interactiveDateTimeFormatter';
import {ConversationTopbarSlaBreachIndicatorSimple} from '../../../viewer/conversation/topbar/conversationTopbarSlaBreachIndicator';

/*
 * Props.
 */

interface CellDateProps {
  date: number;
  reminderDate: number | undefined;
  slaTimerDate: number | undefined;
  waitingSinceMs: number | undefined;
  isSelected: boolean | undefined;
  className?: string;
  isCompact?: boolean;
}

/*
 * Style.
 */

interface DateStyleProps {
  $isSelected: boolean | undefined;
  $hasReminderDate: boolean;
}

const StyledCellDateDiv = styled.div<DateStyleProps>`
  grid-area: top-date;
  display: flex;
  align-items: center;

  text-transform: uppercase;
  font-size: ${fontSizes.tiny};
  line-height: 14px;
  color: ${(p) => getDateColor(p)};

  transform: translateY(0.5px);

  text-align: right;
  justify-self: right;
  margin-right: 4px;

  &:hover {
    color: ${(p) => getDateColor(p, true)};
  }
`;

const StyledInteractiveDateTimeFormatter = styled(InteractiveDateTimeFormatter)`
  width: max-content;
`;
const StyledDateWrapper = styled.div<{isRelative: boolean}>`
  width: ${(p) => (p.isRelative ? '36px' : 'auto')};
  min-width: ${(p) => (p.isRelative ? '0' : '50px')};
  font-variant-numeric: tabular-nums;
`;

function getDateColor(props: DateStyleProps & ThemeContextType, isHovering?: boolean) {
  if (props.$isSelected) {
    return isHovering ? props.theme.invertedText : props.theme.invertedSubtext;
  }

  if (props.$hasReminderDate) {
    return props.theme.palette.orange.shade40;
  }

  return isHovering ? props.theme.greys.shade80 : props.theme.greys.shade70;
}

/*
 * Component.
 */

export const CellDate: FC<CellDateProps> = memo((props) => {
  const interactionId = props.reminderDate ? `cell-date-reminder` : `cell-date`;
  const timeFormat = useAppSelector(getTimeFormat);
  const dateTimeFormat = useAppSelector(getDateTimeFormat);

  const hasSortP2BySlaTimer = useAppSelector(getHasSortP2BySlaTimerFF);

  return (
    <LegacyInteractionParent interactionId={interactionId}>
      <StyledCellDateDiv
        className={props.className}
        $isSelected={props.isSelected}
        $hasReminderDate={Boolean(props.reminderDate)}
      >
        {renderDate(props, timeFormat, dateTimeFormat, hasSortP2BySlaTimer)}
      </StyledCellDateDiv>
    </LegacyInteractionParent>
  );
});

/*
 * Helpers.
 */

function renderDate(
  props: CellDateProps,
  timeFormat: TimeFormatsEnum,
  dateTimeFormat: DateTimeFormatsEnum,
  hasSortP2BySlaTimer: boolean,
) {
  if (props.slaTimerDate && hasSortP2BySlaTimer) {
    return (
      <ConversationTopbarSlaBreachIndicatorSimple
        disableHover
        breachTime={props.slaTimerDate}
        waitingSinceMs={props.waitingSinceMs ?? 0}
      />
    );
  }

  if (props.isCompact) {
    return (
      <StyledDateWrapper isRelative={dateTimeFormat === DateTimeFormatsEnum.RELATIVE}>
        <CompactDateTimeFormatter
          timeFormat={timeFormat}
          dateTimeFormat={dateTimeFormat}
          value={props.reminderDate ?? props.date}
        />
      </StyledDateWrapper>
    );
  }

  // If no reminder date was provided, render the normal date.
  if (!props.reminderDate) {
    return (
      <StyledInteractiveDateTimeFormatter
        relativeFormat={RelativeDateTimeFormatsEnum.SHORT}
        value={props.date}
      />
    );
  }

  // Otherwise, render icon+date.
  return (
    <StyledInteractiveDateTimeFormatter
      relativeFormat={RelativeDateTimeFormatsEnum.SHORT}
      value={props.reminderDate}
    />
  );
}
